import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Spinner,
  SimpleGrid,
  useToast,
  Center,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
} from '@chakra-ui/react';
import { FaSync } from 'react-icons/fa';
import { Navigate } from 'react-router-dom';
import {
  getUserAchievements,
  calculateAchievements,
} from '../api/achievements';
import { useAuth } from '../contexts/AuthContext';

const Achievements = () => {
  const { userData } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unlocked: 0,
    locked: 0,
    totalPoints: 0,
  });
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const toast = useToast();

  // Controllo accesso - solo per utenti con permesso beta-tester
  if (!userData || !userData.permissions?.includes('beta-tester')) {
    return <Navigate to="/" replace />;
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'association-life': '🏛️',
      membership: '🎫',
      events: '🎯',
      donations: '💝',
      subscriptions: '⭐',
      financial: '💰',
      temporal: '⏰',
      social: '🎨',
      explorer: '🗺️',
      special: '✨',
      activities: '📝',
      magic: '🃏',
      gaming: '🎮',
      ranking: '🏅',
      hidden: '✨',
      young: '👶',
      other: '🏆',
    };
    return icons[category] || '🏆';
  };

  const getCategoryDisplayName = (category) => {
    const displayNames = {
      'association-life': 'Vita Associativa',
      membership: 'Tesseramento',
      events: 'Eventi e Partecipazione',
      donations: 'Donazioni e Sostegno',
      subscriptions: 'Abbonamenti',
      financial: 'Investimenti',
      temporal: 'Questione di timing',
      social: 'Social e Community',
      explorer: 'Esplorazione',
      special: 'Speciali e Rari',
      activities: 'Attività e Feedback',
      magic: 'Magic: The Gathering',
      gaming: 'Gaming e Competizioni',
      ranking: 'Ranking e Punti',
      hidden: 'Achievement Speciali',
      young: 'Giovani e Centro Estivo',
      other: 'Altri Achievement',
    };
    return displayNames[category] || 'Altri Achievement';
  };

  // Funzione per determinare se un achievement dovrebbe essere nascosto
  const shouldBeHidden = (achievement) => {
    // Achievement con descrizioni misteriose o che contengono "..."
    const mysteriousDescriptions = [
      'Il primo raggio di sole rivela segreti nascosti...',
      'Quando le stelle brillano, la magia si intensifica...',
      'Certi eventi lasciano un segno indelebile nel cuore...',
      'La natura premia chi la rispetta e la cura...',
      'Le note musicali nascondono un potere segreto...',
      'I veri comandanti lasciano il loro segno...',
      'La semplicità nasconde una forza straordinaria...',
      'Nel duello uno contro uno, solo i migliori sopravvivono...',
      'Costruire un cube richiede visione e creatività...',
      'Il formato più amato richiede maestria e strategia...',
    ];

    return mysteriousDescriptions.some(
      (desc) =>
        achievement.description?.includes(desc) ||
        achievement.congratulations?.includes(desc)
    );
  };

  // Funzione per determinare se un achievement è legato a Magic
  const isMagicRelated = (achievement) => {
    // Achievement con eventCategory MAGIC
    if (achievement.eventCategory === 'MAGIC') {
      return true;
    }

    // Achievement con eventSubCategory Magic
    if (
      achievement.eventSubCategory &&
      achievement.eventSubCategory.toLowerCase().includes('magic')
    ) {
      return true;
    }

    // Achievement con formato Magic specifico
    const magicFormats = [
      'Pauper',
      'Duel Commander',
      'Cube',
      'Commander Tradizionale (EDH)',
      'Standard',
      'Legacy',
    ];

    if (achievement.formato && magicFormats.includes(achievement.formato)) {
      return true;
    }

    // Achievement con nome che contiene "Commander" o "Magic"
    const magicKeywords = [
      'commander',
      'comandante',
      'magic',
      'pauper',
      'cube',
      'standard',
      'legacy',
    ];
    const achievementName = achievement.name?.toLowerCase() || '';
    const achievementDescription = achievement.description?.toLowerCase() || '';
    const achievementCongratulations =
      achievement.congratulations?.toLowerCase() || '';

    return magicKeywords.some(
      (keyword) =>
        achievementName.includes(keyword) ||
        achievementDescription.includes(keyword) ||
        achievementCongratulations.includes(keyword)
    );
  };

  // Funzione helper per determinare se un achievement è completato
  const isAchievementCompleted = (achievement) => {
    return achievement.unlocked;
  };

  // Funzione helper per raggruppare achievement per categoria
  const groupAchievementsByCategory = (achievementsToGroup) => {
    const grouped = achievementsToGroup.reduce((acc, achievement) => {
      // Determina la categoria finale dell'achievement
      let finalCategory = achievement.category || 'other';

      // PRIORITÀ: Se l'achievement è legato a Magic, mettilo nella categoria magic (anche se ha descrizioni misteriose)
      if (isMagicRelated(achievement)) {
        finalCategory = 'magic';
      }
      // Se l'achievement dovrebbe essere nascosto (e NON è Magic), mettilo nella categoria hidden
      else if (shouldBeHidden(achievement)) {
        finalCategory = 'hidden';
      }
      // Raggruppa membership, social e activities sotto "association-life"
      else if (['membership', 'social', 'activities'].includes(finalCategory)) {
        finalCategory = 'association-life';
      }

      if (!acc[finalCategory]) {
        acc[finalCategory] = [];
      }
      acc[finalCategory].push(achievement);
      return acc;
    }, {});

    // Ordina le categorie con Vita Associativa per primo, poi Magic
    const categoryOrder = [
      'association-life',
      'magic',
      'events',
      'donations',
      'subscriptions',
      'financial',
      'temporal',
      'explorer',
      'special',
      'gaming',
      'ranking',
      'hidden',
      'other',
    ];
    const sortedGrouped = {};

    categoryOrder.forEach((category) => {
      if (grouped[category]) {
        // Ordina gli achievement all'interno di ogni categoria per soglia crescente
        sortedGrouped[category] = grouped[category].sort((a, b) => {
          return a.threshold - b.threshold;
        });
      }
    });

    return sortedGrouped;
  };

  // Funzione helper per ottenere l'icona finale di un achievement
  const getFinalAchievementIcon = (achievement) => {
    // Se è nascosto, usa sempre l'icona delle stelline
    if (shouldBeHidden(achievement)) {
      return '✨';
    }

    // Se è Magic, usa l'icona Magic
    if (isMagicRelated(achievement)) {
      return '🃏';
    }

    // Se è Vita Associativa, usa l'icona appropriata
    if (['membership', 'social', 'activities'].includes(achievement.category)) {
      return getCategoryIcon(achievement.category);
    }

    // Per tutti gli altri, usa l'icona della categoria originale
    return getCategoryIcon(achievement.category);
  };

  // Funzione helper per renderizzare gli achievement
  const renderAchievements = (achievementsToRender) => {
    return achievementsToRender.map((achievement) => {
      // Calcola la percentuale di progresso
      let progressPercentage = 0;
      const maxThreshold = achievement.threshold;

      // Calcola percentuale di progresso
      progressPercentage = Math.min(
        (achievement.progress / maxThreshold) * 100,
        100
      );

      // Determina se l'achievement è sbloccato per la visualizzazione
      const isUnlocked = achievement.unlocked;

      return (
        <Box
          key={achievement.key}
          bg={isUnlocked ? '#36393f' : '#2f3136'}
          borderRadius="12px"
          border="1px solid"
          borderColor={isUnlocked ? '#faa61a' : 'gray.600'}
          p={{ base: 3, md: 4 }}
          position="relative"
          transition="all 0.3s ease"
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
          }}
          filter={isUnlocked ? 'none' : 'grayscale(100%)'}
          opacity={isUnlocked ? 1 : 0.7}
          boxShadow="0 4px 20px rgba(0,0,0,0.3)"
        >
          {/* Discord-style Points Badge - Responsive */}
          <Box
            position="absolute"
            top={{ base: '-6px', md: '-8px' }}
            left={{ base: '-6px', md: '-8px' }}
            bg={isUnlocked ? '#faa61a' : '#72767d'}
            color="white"
            borderRadius="12px"
            px={{ base: 2, md: 3 }}
            py={1}
            fontSize={{ base: '2xs', md: 'xs' }}
            fontWeight="700"
            border="2px solid #2f3136"
            boxShadow="0 2px 8px rgba(0,0,0,0.3)"
          >
            {achievement.points} pts
          </Box>

          <VStack spacing={{ base: 2, md: 3 }} align="stretch">
            {/* Discord-style Icon - Responsive */}
            <Box textAlign="center">
              <Box
                boxSize={{ base: '48px', md: '56px' }}
                bg={isUnlocked ? '#faa61a' : '#40444b'}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize={{ base: '24px', md: '28px' }}
                mx="auto"
                border="2px solid"
                borderColor={isUnlocked ? '#faa61a' : '#202225'}
                boxShadow="0 4px 12px rgba(0,0,0,0.2)"
              >
                {getFinalAchievementIcon(achievement)}
              </Box>
            </Box>

            {/* Discord-style Title and Description - Responsive */}
            <VStack spacing={{ base: 1, md: 2 }} align="center">
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="600"
                textAlign="center"
                color={isUnlocked ? 'white' : '#b9bbbe'}
                noOfLines={2}
              >
                {achievement.name}
              </Text>
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                color={isUnlocked ? '#faa61a' : '#72767d'}
                textAlign="center"
                noOfLines={{ base: 3, md: 4 }}
                lineHeight="1.4"
                fontWeight={isUnlocked ? '500' : 'normal'}
              >
                {isUnlocked
                  ? achievement.congratulations ||
                    achievement.description ||
                    `Completa ${achievement.threshold} ${achievement.category}`
                  : achievement.description ||
                    `Completa ${achievement.threshold} ${achievement.category}`}
              </Text>
            </VStack>

            {/* Progress Bar - Mostra solo se non completato */}
            {progressPercentage < 100 && (
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between" fontSize="xs">
                  <Text color="#72767d" fontWeight="500">
                    Progresso
                  </Text>
                  <Text color="#b9bbbe" fontWeight="600">
                    {achievement.progress}/{maxThreshold}
                  </Text>
                </HStack>
                <Box
                  bg="#202225"
                  borderRadius="4px"
                  h="8px"
                  overflow="hidden"
                  border="1px solid #40444b"
                >
                  <Box
                    bg={isUnlocked ? '#faa61a' : '#5865f2'}
                    h="100%"
                    width={`${progressPercentage}%`}
                    transition="width 0.3s ease"
                  />
                </Box>
                <Text
                  fontSize="xs"
                  color="#b9bbbe"
                  textAlign="center"
                  fontWeight="500"
                >
                  {Math.round(progressPercentage)}% completato
                </Text>
              </VStack>
            )}

            {/* Discord-style Unlocked Date - Responsive */}
            {isUnlocked && achievement.unlockedAt && (
              <Text
                fontSize={{ base: '2xs', md: 'xs' }}
                color="#faa61a"
                textAlign="center"
                fontWeight="500"
                bg="#2f3136"
                px={{ base: 1, md: 2 }}
                py={1}
                borderRadius="6px"
                border="1px solid #faa61a"
              >
                ✓ {new Date(achievement.unlockedAt).toLocaleDateString('it-IT')}
              </Text>
            )}
          </VStack>
        </Box>
      );
    });
  };

  const loadAchievements = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getUserAchievements();
      if (response.success) {
        setAchievements(response.data);
        setStats(response.stats);
      } else {
        setError('Errore nel caricamento degli achievement');
      }
    } catch (err) {
      console.error('Error loading achievements:', err);
      setError('Errore nel caricamento degli achievement');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAchievements = async () => {
    try {
      setChecking(true);
      const response = await calculateAchievements();

      if (response.success) {
        const newCount = response.data.count;
        if (newCount > 0) {
          toast({
            title: 'Nuovi Achievement Sbloccati!',
            description: `Hai sbloccato ${newCount} nuovi achievement!`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Controllo Completato',
            description: 'Nessun nuovo achievement sbloccato.',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        }

        // Ricarica gli achievement
        await loadAchievements();
      } else {
        toast({
          title: 'Errore',
          description:
            response.message || 'Errore nel controllo degli achievement',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error checking achievements:', err);
      toast({
        title: 'Errore',
        description: 'Errore nel controllo degli achievement',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    loadAchievements();
  }, []);

  if (loading) {
    return (
      <Box bg="#2f3136" minH="100vh" color="white">
        <Center h="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="#5865f2" />
            <Text color="#b9bbbe" fontSize="md" fontWeight="500">
              Caricamento achievement...
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="#2f3136" minH="100vh" color="white" p={6}>
        <Box maxW="600px" mx="auto">
          <Box
            bg="#f04747"
            color="white"
            p={4}
            borderRadius="8px"
            border="1px solid #d83c3e"
            mb={4}
          >
            <HStack>
              <Text fontSize="lg">⚠️</Text>
              <Text fontWeight="500">{error}</Text>
            </HStack>
          </Box>
          <Button
            onClick={loadAchievements}
            bg="#5865f2"
            color="white"
            _hover={{ bg: '#4752c4' }}
            borderRadius="4px"
            fontWeight="500"
          >
            Riprova
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box bg="#2f3136" minH="100vh" p={{ base: 6, md: 8 }} color="white">
      <VStack spacing={6} align="stretch" maxW="1200px" mx="auto">
        {/* Discord-style Header with Check Button */}
        <Box position="relative" mb={4}>
          {/* Check Button - Top Right (Hidden on mobile) */}
          <Box
            position="absolute"
            top={0}
            right={0}
            display={{ base: 'none', md: 'block' }}
          >
            <Button
              onClick={handleCheckAchievements}
              isLoading={checking}
              loadingText="Controllando..."
              bg="#5865f2"
              color="white"
              size="sm"
              leftIcon={<FaSync />}
              _hover={{ bg: '#4752c4' }}
              _active={{ bg: '#3c45a5' }}
              borderRadius="6px"
              fontWeight="500"
              px={3}
              py={2}
              fontSize="sm"
              boxShadow="0 2px 8px rgba(88, 101, 242, 0.3)"
            >
              Controlla
            </Button>
          </Box>

          {/* Header Content */}
          <Box textAlign="center" pr={{ base: 0, md: '120px' }}>
            <Heading size="lg" color="white" mb={2} fontWeight="800">
              🏆 Achievement System
            </Heading>
            <Text color="gray.300" fontSize="md">
              Completa le attività per sbloccare achievement e guadagnare punti!
            </Text>
          </Box>

          {/* Mobile Check Button - Below Header */}
          <Box
            textAlign="center"
            mt={4}
            display={{ base: 'block', md: 'none' }}
          >
            <Button
              onClick={handleCheckAchievements}
              isLoading={checking}
              loadingText="Controllando..."
              bg="#5865f2"
              color="white"
              size="sm"
              leftIcon={<FaSync />}
              _hover={{ bg: '#4752c4' }}
              _active={{ bg: '#3c45a5' }}
              borderRadius="6px"
              fontWeight="500"
              px={4}
              py={2}
              fontSize="sm"
              boxShadow="0 2px 8px rgba(88, 101, 242, 0.3)"
            >
              Controlla Achievement
            </Button>
          </Box>
        </Box>

        {/* Discord-style Stats - Responsive */}
        <Box
          bg="#36393f"
          p={{ base: 4, md: 6 }}
          borderRadius="12px"
          border="1px solid"
          borderColor="gray.600"
          boxShadow="0 4px 20px rgba(0,0,0,0.3)"
        >
          <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={{ base: 4, md: 6 }}>
            <Box textAlign="center">
              <Text
                color="gray.300"
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="600"
                mb={2}
              >
                TOTALE
              </Text>
              <Text
                color="#5865f2"
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="700"
              >
                {stats.total}
              </Text>
              <Text color="gray.400" fontSize={{ base: 'xs', sm: 'xs' }}>
                Achievement
              </Text>
            </Box>
            <Box textAlign="center">
              <Text
                color="gray.300"
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="600"
                mb={2}
              >
                SBLOCCATI
              </Text>
              <Text
                color="#43b581"
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="700"
              >
                {stats.unlocked}
              </Text>
              <Text color="gray.400" fontSize={{ base: 'xs', sm: 'xs' }}>
                {stats.total > 0
                  ? Math.round((stats.unlocked / stats.total) * 100)
                  : 0}
                % completato
              </Text>
            </Box>
            <Box textAlign="center">
              <Text
                color="gray.300"
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="600"
                mb={2}
              >
                BLOCCATI
              </Text>
              <Text
                color="#f04747"
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="700"
              >
                {stats.locked}
              </Text>
              <Text color="gray.400" fontSize={{ base: 'xs', sm: 'xs' }}>
                Da sbloccare
              </Text>
            </Box>
            <Box textAlign="center">
              <Text
                color="gray.300"
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="600"
                mb={2}
              >
                PUNTI TOTALI
              </Text>
              <Text
                color="#faa61a"
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="700"
              >
                {stats.totalPoints}
              </Text>
              <Text color="gray.400" fontSize={{ base: 'xs', sm: 'xs' }}>
                Guadagnati
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Minimalist Responsive Tabs */}
        <Tabs
          index={activeTab}
          onChange={setActiveTab}
          variant="line"
          size="sm"
        >
          <TabList
            borderBottom="2px solid"
            borderColor="gray.600"
            justifyContent="center"
            flexWrap="wrap"
            gap={{ base: 1, md: 2 }}
          >
            <Tab
              color="gray.400"
              fontWeight="500"
              fontSize={{ base: 'sm', md: 'md' }}
              px={{ base: 3, md: 4 }}
              py={{ base: 2, md: 3 }}
              _selected={{
                color: '#5865f2',
                borderBottomColor: '#5865f2',
                fontWeight: '600',
              }}
              _hover={{ color: 'white' }}
              borderBottom="2px solid transparent"
              transition="all 0.2s"
            >
              <HStack spacing={{ base: 1, md: 2 }}>
                <Text fontSize={{ base: 'sm', md: 'md' }}>Tutti</Text>
                <Badge
                  bg="#5865f2"
                  color="white"
                  borderRadius="full"
                  fontSize={{ base: '2xs', md: 'xs' }}
                  px={{ base: 1, md: 2 }}
                  py={{ base: 0.5, md: 1 }}
                  minW={{ base: '16px', md: '20px' }}
                  textAlign="center"
                >
                  {stats.total}
                </Badge>
              </HStack>
            </Tab>
            <Tab
              color="gray.400"
              fontWeight="500"
              fontSize={{ base: 'sm', md: 'md' }}
              px={{ base: 3, md: 4 }}
              py={{ base: 2, md: 3 }}
              _selected={{
                color: '#43b581',
                borderBottomColor: '#43b581',
                fontWeight: '600',
              }}
              _hover={{ color: 'white' }}
              borderBottom="2px solid transparent"
              transition="all 0.2s"
            >
              <HStack spacing={{ base: 1, md: 2 }}>
                <Text fontSize={{ base: 'sm', md: 'md' }}>Completati</Text>
                <Badge
                  bg="#43b581"
                  color="white"
                  borderRadius="full"
                  fontSize={{ base: '2xs', md: 'xs' }}
                  px={{ base: 1, md: 2 }}
                  py={{ base: 0.5, md: 1 }}
                  minW={{ base: '16px', md: '20px' }}
                  textAlign="center"
                >
                  {stats.unlocked}
                </Badge>
              </HStack>
            </Tab>
            <Tab
              color="gray.400"
              fontWeight="500"
              fontSize={{ base: 'sm', md: 'md' }}
              px={{ base: 3, md: 4 }}
              py={{ base: 2, md: 3 }}
              _selected={{
                color: '#f04747',
                borderBottomColor: '#f04747',
                fontWeight: '600',
              }}
              _hover={{ color: 'white' }}
              borderBottom="2px solid transparent"
              transition="all 0.2s"
            >
              <HStack spacing={{ base: 1, md: 2 }}>
                <Text fontSize={{ base: 'sm', md: 'md' }}>Da Sbloccare</Text>
                <Badge
                  bg="#f04747"
                  color="white"
                  borderRadius="full"
                  fontSize={{ base: '2xs', md: 'xs' }}
                  px={{ base: 1, md: 2 }}
                  py={{ base: 0.5, md: 1 }}
                  minW={{ base: '16px', md: '20px' }}
                  textAlign="center"
                >
                  {stats.locked}
                </Badge>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels mt={4}>
            {/* Tab: Tutti gli Achievement */}
            <TabPanel p={0}>
              <VStack spacing={8} align="stretch">
                {Object.entries(
                  groupAchievementsByCategory(
                    achievements.sort((a, b) => {
                      // Prima gli achievement completati (unlocked: true), poi quelli non completati
                      if (a.unlocked && !b.unlocked) return -1;
                      if (!a.unlocked && b.unlocked) return 1;
                      return 0;
                    })
                  )
                ).map(([category, categoryAchievements]) => (
                  <Box key={category}>
                    {/* Header Categoria */}
                    <HStack spacing={3} mb={4} align="center">
                      <Box
                        boxSize="40px"
                        bg="#36393f"
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="20px"
                        border="2px solid"
                        borderColor="gray.600"
                        boxShadow="0 2px 8px rgba(0,0,0,0.2)"
                      >
                        {getCategoryIcon(category)}
                      </Box>
                      <VStack spacing={0} align="start">
                        <Text fontSize="lg" fontWeight="700" color="white">
                          {getCategoryDisplayName(category)}
                        </Text>
                        <Text fontSize="sm" color="gray.400" fontWeight="500">
                          {categoryAchievements.length} achievement
                        </Text>
                      </VStack>
                    </HStack>

                    {/* Achievement della categoria */}
                    <SimpleGrid
                      columns={{ base: 1, sm: 2, lg: 3 }}
                      spacing={{ base: 4, md: 6 }}
                    >
                      {renderAchievements(categoryAchievements)}
                    </SimpleGrid>
                  </Box>
                ))}
              </VStack>
            </TabPanel>

            {/* Tab: Achievement Completati */}
            <TabPanel p={0}>
              {achievements.filter((a) => isAchievementCompleted(a)).length >
              0 ? (
                <SimpleGrid
                  columns={{ base: 1, sm: 2, lg: 3 }}
                  spacing={{ base: 4, md: 6 }}
                >
                  {renderAchievements(
                    achievements.filter((a) => isAchievementCompleted(a))
                  )}
                </SimpleGrid>
              ) : (
                <Center py={12}>
                  <VStack spacing={4}>
                    <Text fontSize="4xl">🎯</Text>
                    <Text color="#72767d" fontSize="lg" fontWeight="500">
                      Nessun achievement completato ancora
                    </Text>
                    <Text color="#72767d" fontSize="sm" textAlign="center">
                      Continua a partecipare alle attività per sbloccare i tuoi
                      primi achievement!
                    </Text>
                  </VStack>
                </Center>
              )}
            </TabPanel>

            {/* Tab: Achievement da Sbloccare */}
            <TabPanel p={0}>
              {achievements.filter((a) => !isAchievementCompleted(a)).length >
              0 ? (
                <SimpleGrid
                  columns={{ base: 1, sm: 2, lg: 3 }}
                  spacing={{ base: 4, md: 6 }}
                >
                  {renderAchievements(
                    achievements.filter((a) => !isAchievementCompleted(a))
                  )}
                </SimpleGrid>
              ) : (
                <Center py={12}>
                  <VStack spacing={4}>
                    <Text fontSize="4xl">🏆</Text>
                    <Text color="#72767d" fontSize="lg" fontWeight="500">
                      Tutti gli achievement sono stati sbloccati!
                    </Text>
                    <Text color="#72767d" fontSize="sm" textAlign="center">
                      Complimenti! Hai completato tutti gli achievement
                      disponibili.
                    </Text>
                  </VStack>
                </Center>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>

        {achievements.length === 0 && (
          <Center py={8}>
            <Text color="#72767d" fontSize="md" fontWeight="500">
              Nessun achievement disponibile
            </Text>
          </Center>
        )}
      </VStack>
    </Box>
  );
};

export default Achievements;
